/**
 * TERMINAL STORE
 *
 * Zustand store for managing terminal sessions and WebSocket connections.
 * Handles real-time communication with PTY sessions on the backend.
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export type TerminalStatus = "connecting" | "connected" | "disconnected" | "error" | "waking";

export interface TerminalTab {
  id: string;
  sessionId: string | null;
  projectId: number;
  name: string;
  status: TerminalStatus;
  error?: string;
  outputBuffer: string;
  // Absolute offset of the first char currently stored in outputBuffer.
  // Prevents UI desync when old output is trimmed.
  bufferOffset: number;
}

interface TerminalState {
  // State
  tabs: Record<number, TerminalTab[]>; // projectId -> tabs
  activeTabId: Record<number, string>; // projectId -> active tab id
  isLoading: boolean;
  error: string | null;

  // WebSocket connections (not persisted)
  connections: Record<string, WebSocket>; // sessionId -> WebSocket

  // Actions
  createTab: (projectId: number, name?: string) => string;
  closeTab: (projectId: number, tabId: string) => void;
  setActiveTab: (projectId: number, tabId: string) => void;
  renameTab: (projectId: number, tabId: string, name: string) => void;
  updateTabStatus: (projectId: number, tabId: string, status: TerminalStatus, error?: string) => void;
  setSessionId: (projectId: number, tabId: string, sessionId: string) => void;

  // WebSocket management
  connect: (
    projectId: number,
    tabId: string,
    token: string,
    onOutput?: (data: string) => void,
    onSessionCreated?: (sessionId: string) => void,
    command?: string
  ) => void;
  disconnect: (projectId: number, tabId: string) => void;
  sendInput: (projectId: number, tabId: string, data: string) => void;
  sendResize: (projectId: number, tabId: string, cols: number, rows: number) => void;

  // Utilities
  getTabsForProject: (projectId: number) => TerminalTab[];
  getActiveTab: (projectId: number) => TerminalTab | null;
  getConnection: (sessionId: string) => WebSocket | null;
}

// Generate unique tab IDs
let tabIdCounter = 0;
const generateTabId = () => `term-${Date.now()}-${++tabIdCounter}`;
const getResizeKey = (projectId: number, tabId: string) => `${projectId}::${tabId}`;
const lastSentResizes = new Map<string, { cols: number; rows: number }>();
const MAX_OUTPUT_BUFFER = 5_000_000;

function appendOutputToBuffer(
  currentBuffer: string,
  currentOffset: number,
  chunk: string
): { outputBuffer: string; bufferOffset: number } {
  if (!chunk) {
    return {
      outputBuffer: currentBuffer,
      bufferOffset: currentOffset,
    };
  }

  const combined = currentBuffer + chunk;
  if (combined.length <= MAX_OUTPUT_BUFFER) {
    return {
      outputBuffer: combined,
      bufferOffset: currentOffset,
    };
  }

  const trimBy = combined.length - MAX_OUTPUT_BUFFER;
  return {
    outputBuffer: combined.slice(trimBy),
    bufferOffset: currentOffset + trimBy,
  };
}

// WebSocket URL helper — connects to Lightsail terminal server
const getTerminalHost = (): string => {
  if (import.meta.env.VITE_TERMINAL_HOST) {
    return import.meta.env.VITE_TERMINAL_HOST;
  }
  return "terminal.colony.so";
};

const getNewSessionWebSocketUrl = (projectId: number, token: string, cols: number = 80, rows: number = 24, command?: string): string => {
  const host = getTerminalHost();
  let url = `wss://${host}/ws/terminal/new/${projectId}?token=${encodeURIComponent(token)}&cols=${cols}&rows=${rows}`;
  if (command) {
    url += `&command=${encodeURIComponent(command)}`;
  }
  return url;
};

const getExistingSessionWebSocketUrl = (sessionId: string, token: string): string => {
  const host = getTerminalHost();
  return `wss://${host}/ws/terminal/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token)}`;
};

const getCloudTerminalWebSocketUrl = (
  workspaceId: number,
  projectId: number,
  token: string,
  cols: number = 80,
  rows: number = 24,
  command?: string,
): string => {
  const host = getTerminalHost();
  let url = `wss://${host}/ws/terminal/new/${projectId}?token=${encodeURIComponent(token)}&cols=${cols}&rows=${rows}`;
  if (command) {
    url += `&command=${encodeURIComponent(command)}`;
  }
  return url;
};

export const useTerminalStore = create<TerminalState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    tabs: {},
    activeTabId: {},
    isLoading: false,
    error: null,
    connections: {},

    // Create a new terminal tab
    createTab: (projectId: number, name?: string): string => {
      const tabId = generateTabId();
      const existingTabs = get().tabs[projectId] || [];
      const tabNumber = existingTabs.length + 1;

      const newTab: TerminalTab = {
        id: tabId,
        sessionId: null,
        projectId,
        name: name || `Terminal ${tabNumber}`,
        status: "disconnected",
        outputBuffer: "",
        bufferOffset: 0,
      };

      set((state) => ({
        tabs: {
          ...state.tabs,
          [projectId]: [...(state.tabs[projectId] || []), newTab],
        },
        activeTabId: {
          ...state.activeTabId,
          [projectId]: tabId,
        },
      }));

      return tabId;
    },

    // Close a terminal tab
    closeTab: (projectId: number, tabId: string) => {
      const state = get();
      const tabs = state.tabs[projectId] || [];
      const tab = tabs.find((t) => t.id === tabId);
      lastSentResizes.delete(getResizeKey(projectId, tabId));

      // Disconnect WebSocket if connected
      if (tab?.sessionId && state.connections[tab.sessionId]) {
        state.connections[tab.sessionId].close();
      }

      const newTabs = tabs.filter((t) => t.id !== tabId);
      let newActiveId = state.activeTabId[projectId];

      // If closing active tab, switch to another
      if (newActiveId === tabId && newTabs.length > 0) {
        newActiveId = newTabs[newTabs.length - 1].id;
      }

      set((state) => ({
        tabs: {
          ...state.tabs,
          [projectId]: newTabs,
        },
        activeTabId: {
          ...state.activeTabId,
          [projectId]: newActiveId,
        },
        connections: tab?.sessionId
          ? Object.fromEntries(
              Object.entries(state.connections).filter(([k]) => k !== tab.sessionId)
            )
          : state.connections,
      }));
    },

    // Set active tab
    setActiveTab: (projectId: number, tabId: string) => {
      set((state) => ({
        activeTabId: {
          ...state.activeTabId,
          [projectId]: tabId,
        },
      }));
    },

    // Rename a tab
    renameTab: (projectId: number, tabId: string, name: string) => {
      set((state) => ({
        tabs: {
          ...state.tabs,
          [projectId]: (state.tabs[projectId] || []).map((tab) =>
            tab.id === tabId ? { ...tab, name } : tab
          ),
        },
      }));
    },

    // Update tab status
    updateTabStatus: (projectId: number, tabId: string, status: TerminalStatus, error?: string) => {
      set((state) => ({
        tabs: {
          ...state.tabs,
          [projectId]: (state.tabs[projectId] || []).map((tab) =>
            tab.id === tabId ? { ...tab, status, error } : tab
          ),
        },
      }));
    },

    // Set session ID for a tab
    setSessionId: (projectId: number, tabId: string, sessionId: string) => {
      set((state) => ({
        tabs: {
          ...state.tabs,
          [projectId]: (state.tabs[projectId] || []).map((tab) =>
            tab.id === tabId ? { ...tab, sessionId } : tab
          ),
        },
      }));
    },

    // Connect WebSocket for a terminal tab
    connect: (
      projectId: number,
      tabId: string,
      token: string,
      onOutput?: (data: string) => void,
      onSessionCreated?: (sessionId: string) => void,
      command?: string
    ) => {
      const state = get();
      const tabs = state.tabs[projectId] || [];
      const tab = tabs.find((t) => t.id === tabId);

      if (!tab) {
        console.error(`Tab ${tabId} not found for project ${projectId}`);
        return;
      }

      if (tab.status === "connecting") {
        return;
      }

      // Already connected?
      if (tab.sessionId && state.connections[tab.sessionId]) {
        return;
      }

      // Update status to connecting
      get().updateTabStatus(projectId, tabId, "connecting");
      lastSentResizes.delete(getResizeKey(projectId, tabId));

      const existingSessionId = tab.sessionId && !command ? tab.sessionId : null;

      // Create WebSocket connection
      const wsUrl = existingSessionId
        ? getExistingSessionWebSocketUrl(existingSessionId, token)
        : getNewSessionWebSocketUrl(projectId, token, 80, 24, command);
      const ws = new WebSocket(wsUrl);
      let socketSessionId: string | null = existingSessionId;
      let pendingOutput = "";
      let flushTimer: ReturnType<typeof setTimeout> | null = null;

      const appendOutputChunk = (chunk: string) => {
        if (!chunk) return;

        let applied = false;
        set((currentState) => {
          const projectTabs = currentState.tabs[projectId] || [];
          const nextTabs = projectTabs.map((candidate) => {
            if (candidate.id !== tabId) return candidate;
            if (socketSessionId && candidate.sessionId !== socketSessionId) return candidate;

            const appended = appendOutputToBuffer(candidate.outputBuffer, candidate.bufferOffset, chunk);
            applied = true;
            return {
              ...candidate,
              outputBuffer: appended.outputBuffer,
              bufferOffset: appended.bufferOffset,
            };
          });

          if (!applied) return currentState;
          return {
            tabs: {
              ...currentState.tabs,
              [projectId]: nextTabs,
            },
          };
        });

        if (applied) {
          onOutput?.(chunk);
        }
      };

      const flushPendingOutput = () => {
        flushTimer = null;
        if (!pendingOutput) return;
        const chunk = pendingOutput;
        pendingOutput = "";
        appendOutputChunk(chunk);
      };

      const queueOutput = (chunk: string) => {
        if (!chunk) return;
        pendingOutput += chunk;
        if (!flushTimer) {
          flushTimer = setTimeout(flushPendingOutput, 16);
        }
      };

      ws.onopen = () => {
        console.log(`Terminal WebSocket connected for tab ${tabId}`);

        // Re-attach to existing session (e.g. after UI unmount/remount).
        if (socketSessionId) {
          const currentTab = get().tabs[projectId]?.find((t) => t.id === tabId);
          if (!currentTab || currentTab.sessionId !== socketSessionId) {
            ws.close();
            return;
          }

          get().updateTabStatus(projectId, tabId, "connected");
          set((currentState) => ({
            connections: {
              ...currentState.connections,
              [socketSessionId!]: ws,
            },
          }));

          if (onSessionCreated) {
            onSessionCreated(socketSessionId);
          }
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case "waking":
              // Workspace is waking up — show spinner, auto-retry
              get().updateTabStatus(projectId, tabId, "waking");
              ws.close();
              // Retry connection after ETA
              setTimeout(() => {
                const currentState = get();
                const retryTab = currentState.tabs[projectId]?.find((t) => t.id === tabId);
                if (retryTab?.status === "waking") {
                  get().connect(projectId, tabId, token, onOutput, onSessionCreated, command);
                }
              }, (msg.eta_seconds || 30) * 1000);
              return;

            case "session_created":
              // Store session ID and mark as connected
              socketSessionId = msg.session_id;
              get().setSessionId(projectId, tabId, msg.session_id);
              get().updateTabStatus(projectId, tabId, "connected");

              // Store WebSocket connection
              set((state) => ({
                connections: {
                  ...state.connections,
                  [msg.session_id]: ws,
                },
              }));

              if (onSessionCreated) {
                onSessionCreated(msg.session_id);
              }
              break;

            case "output":
              queueOutput(msg.data ?? "");
              break;

            case "exit":
              flushPendingOutput();
              if (!socketSessionId) {
                get().updateTabStatus(projectId, tabId, "disconnected");
                break;
              }
              if (get().tabs[projectId]?.find((t) => t.id === tabId)?.sessionId === socketSessionId) {
                get().updateTabStatus(projectId, tabId, "disconnected");
              }
              break;

            case "error":
              flushPendingOutput();
              if (!socketSessionId) {
                get().updateTabStatus(projectId, tabId, "error", msg.message);
                break;
              }
              if (get().tabs[projectId]?.find((t) => t.id === tabId)?.sessionId === socketSessionId) {
                get().updateTabStatus(projectId, tabId, "error", msg.message);
              }
              break;

            case "pong":
              // Heartbeat response, ignore
              break;
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };

      ws.onerror = (event) => {
        console.error("Terminal WebSocket error:", event);
        flushPendingOutput();
        const currentTab = get().tabs[projectId]?.find((t) => t.id === tabId);
        if (!currentTab) return;
        if (socketSessionId) {
          if (currentTab.sessionId !== socketSessionId) return;
        } else if (currentTab.status !== "connecting" || currentTab.sessionId) {
          return;
        }
        get().updateTabStatus(projectId, tabId, "error", "Connection error");
      };

      ws.onclose = (event) => {
        console.log(`Terminal WebSocket closed: ${event.code} ${event.reason}`);
        if (flushTimer) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        flushPendingOutput();

        const currentTab = get().tabs[projectId]?.find((t) => t.id === tabId);
        const socketStillActive = currentTab
          ? socketSessionId
            ? currentTab.sessionId === socketSessionId
            : currentTab.status === "connecting" && !currentTab.sessionId
          : false;
        if (socketStillActive && currentTab?.status !== "error") {
          get().updateTabStatus(projectId, tabId, "disconnected");
        }

        // Remove from connections
        if (socketSessionId) {
          set((state) => ({
            connections: Object.fromEntries(
              Object.entries(state.connections).filter(([k]) => k !== socketSessionId)
            ),
          }));
        }
      };
    },

    // Disconnect WebSocket
    disconnect: (projectId: number, tabId: string) => {
      const state = get();
      const tab = state.tabs[projectId]?.find((t) => t.id === tabId);

      if (tab?.sessionId && state.connections[tab.sessionId]) {
        state.connections[tab.sessionId].close();
      }
    },

    // Send input to terminal
    sendInput: (projectId: number, tabId: string, data: string) => {
      const state = get();
      const tab = state.tabs[projectId]?.find((t) => t.id === tabId);

      if (tab?.sessionId && state.connections[tab.sessionId]) {
        const ws = state.connections[tab.sessionId];
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "input", data }));
        }
      }
    },

    // Send resize to terminal
    sendResize: (projectId: number, tabId: string, cols: number, rows: number) => {
      if (cols < 2 || rows < 2) {
        return;
      }

      const state = get();
      const tab = state.tabs[projectId]?.find((t) => t.id === tabId);

      if (tab?.sessionId && state.connections[tab.sessionId]) {
        const ws = state.connections[tab.sessionId];
        if (ws.readyState === WebSocket.OPEN) {
          const resizeKey = getResizeKey(projectId, tabId);
          const previous = lastSentResizes.get(resizeKey);
          if (previous?.cols === cols && previous.rows === rows) {
            return;
          }
          lastSentResizes.set(resizeKey, { cols, rows });
          ws.send(JSON.stringify({ type: "resize", cols, rows }));
        }
      }
    },

    // Get tabs for a project
    getTabsForProject: (projectId: number): TerminalTab[] => {
      return get().tabs[projectId] || [];
    },

    // Get active tab for a project
    getActiveTab: (projectId: number): TerminalTab | null => {
      const state = get();
      const tabs = state.tabs[projectId] || [];
      const activeId = state.activeTabId[projectId];
      return tabs.find((t) => t.id === activeId) || null;
    },

    // Get WebSocket connection for a session
    getConnection: (sessionId: string): WebSocket | null => {
      return get().connections[sessionId] || null;
    },
  }))
);
